// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Company listing customers view route module.
 * Displays only companies with customer role (currentView='customers').
 *
 * This route renders the company list with customer role filtering,
 * showing only companies that are customers.
 */
import { PageContent } from '@/components/page-content';
import { crmSyncStatusSchema } from '@/constants/crm-sync-status';
import { sortDirectionSchema } from '@/constants/sort';
import { CustomerFilter } from '@/customer/api/get-customers';
import { CompanyList } from '@/customer/components/company-list';
import { customerSortPropertySchema } from '@/customer/hooks/use-customers';
import { searchTermSchema } from '@/utilities/search-term';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

/**
 * Schema for validating URL search parameters.
 * Ensures type safety for route parameters.
 */
const searchSchema = z.object({
  query: searchTermSchema,
  archived: z.boolean().optional(),
  syncStatuses: z.array(crmSyncStatusSchema).optional(),
  direction: sortDirectionSchema.optional(),
  sort: customerSortPropertySchema.optional(),
  page: z.number().optional(),
  new: z.string().optional(),
});

/**
 * Company customers view route configuration.
 * Sets up the component and search parameter validation.
 */
export const Route = createFileRoute('/_app/companies/customers/')({
  component: CompaniesCustomersIndexRoute,
  validateSearch: searchSchema,
});

/**
 * Company customers view route component.
 * Displays only companies with customer role.
 */
function CompaniesCustomersIndexRoute() {
  const search = Route.useSearch();

  /**
   * Filter configuration for showing only customers.
   * Applies customer role filter (isCustomer=true).
   */
  const filter: CustomerFilter = {
    isArchived: search.archived ? 'all' : undefined,
    query: search.query,
    syncStatuses: search.syncStatuses,
    isCustomer: true,
  };

  return (
    <PageContent>
      <CompanyList currentView="customers" filter={filter} search={search} />
    </PageContent>
  );
}
